import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { authApi } from '../api/client';

interface Tenant {
  slug: string;
  name: string;
  code?: string;
}

interface TenantContextType {
  currentTenant: Tenant | null;
  isLoading: boolean;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const initialLoadDone = useRef(false);

  const fetchTenant = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCurrentTenant(null);
      setIsLoading(false);
      initialLoadDone.current = true;
      return;
    }
    // Only show loading spinner on initial load, not refreshes
    if (!initialLoadDone.current) {
      setIsLoading(true);
    }
    try {
      const res = await authApi.getTenant();
      setCurrentTenant({
        slug: res.data.slug,
        name: res.data.name,
        code: res.data.code,
      });
    } catch {
      setCurrentTenant(null);
    } finally {
      setIsLoading(false);
      initialLoadDone.current = true;
    }
  }, []);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  // Re-fetch when token changes (listen to storage events for cross-tab)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'token') fetchTenant();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchTenant]);

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        isLoading,
        refreshTenant: fetchTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};
